import { useState } from 'react';
import { Button } from '../../../../../components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../../../components/ui/dialog';
import { useQuery } from '@tanstack/react-query';
import { lessonsAPI } from '../../../api/lessons.api';
import type { LessonAnswerReportDTO, LessonDTO } from '../../../core/types/lesson.types';
import { ApprovalStatusBadge } from './ApprovalStatusBadge';
import { UserInfoCard } from './UserInfoCard';
import { AnswerSlidesList } from './AnswerSlidesList';
import { ApprovalDialog } from '../../../components/ApprovalDialog';
import { RejectionDialog } from '../../../../../shared/components/dialogs/RejectionDialog';
import { ActionButton } from '../../../../../shared/components/buttons/ActionButton';
import { useLessonAnswersMutations } from '../../../hooks/use-lesson-answers-mutations';
import { lessonTypeConfigs } from '../../../config/lesson-types.config';

interface AnswerDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    answer: LessonAnswerReportDTO | null;
    lesson: LessonDTO | null;
    userRole?: string;
    onActionComplete?: () => void;
}

export function AnswerDetailsDialog({
                                        open,
                                        onClose,
                                        answer,
                                        lesson,
                                        userRole,
                                        onActionComplete,
                                    }: AnswerDetailsDialogProps) {
    const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
    const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);

    const { data: answers, isLoading: loadingAnswers, refetch } = useQuery({
        queryKey: ['lesson-answer-submission', answer?.id],
        queryFn: () => lessonsAPI.getAnswersForSubmission(answer!.id!),
        enabled: Boolean(answer?.id) && open,
    });

    const mutations = useLessonAnswersMutations();

    const typeAlias = answer?.lesson?.type;
    const config = typeAlias
        ? lessonTypeConfigs[typeAlias as keyof typeof lessonTypeConfigs]
        : null;
    const requiresApproval = config?.features.requiresApproval ?? false;
    const isPendingApproval = answer?.state === 'PENDING_APPROVAL';
    const canApprove =
        userRole &&
        (userRole === 'ADMIN' || userRole === 'DBEST') &&
        requiresApproval &&
        isPendingApproval;

    const handleOpenApprovalDialog = () => {
        setIsApprovalDialogOpen(true);
    };

    const handleConfirmApproval = async (value: { points?: number; fise?: number }) => {
        if (!answer?.id) return;
        await mutations.approve.mutateAsync({
            id: answer.id,
            ...value,
        });
        setIsApprovalDialogOpen(false);
        await refetch();
        onActionComplete?.();
    };

    const handleOpenRejectDialog = () => {
        setIsRejectionDialogOpen(true);
    };

    const handleConfirmReject = async (reason: string) => {
        if (!answer?.id) return;
        await mutations.reject.mutateAsync({
            id: answer.id,
            reason: reason || 'Răspuns respins',
        });
        setIsRejectionDialogOpen(false);
        await refetch();
        onActionComplete?.();
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onClose}>
                {answer && lesson ? (
                    <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
                        <DialogHeader>
                            <DialogTitle>Răspunsuri - {lesson.title}</DialogTitle>
                            <DialogDescription>
                                Detalii despre răspunsurile trimise de utilizator
                            </DialogDescription>
                        </DialogHeader>

                        <div className="flex-1 overflow-y-auto ecotic-form-scrollbar space-y-6 py-4 pr-2">
                            <UserInfoCard
                                user={answer.user}
                                timestamp={answer.timestamp}
                                points={answer.points}
                                version={answer.version}
                                participants={answer.participants}
                            />

                            <AnswerSlidesList
                                lesson={lesson}
                                answers={answers}
                                loading={loadingAnswers}
                            />

                            <ApprovalStatusBadge
                                state={answer.state as any}
                                rejectReason={answer.rejectReason}
                            />
                        </div>

                        <DialogFooter>
                            {canApprove && (
                                <>
                                    <ActionButton
                                        intent="approve"
                                        size="sm"
                                        onClick={handleOpenApprovalDialog}
                                        disabled={mutations.isApproving || mutations.isRejecting}
                                        isLoading={mutations.isApproving}
                                    >
                                        Aprobă
                                    </ActionButton>
                                    <ActionButton
                                        intent="reject"
                                        size="sm"
                                        onClick={handleOpenRejectDialog}
                                        disabled={mutations.isApproving || mutations.isRejecting}
                                        isLoading={mutations.isRejecting}
                                    >
                                        Respinge
                                    </ActionButton>
                                </>
                            )}
                            <Button onClick={onClose} variant="outline">
                                Închide
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                ) : null}
            </Dialog>

            <ApprovalDialog
                isOpen={isApprovalDialogOpen}
                onClose={() => setIsApprovalDialogOpen(false)}
                onApprove={handleConfirmApproval}
                lessonAnswer={answer}
                loading={mutations.isApproving}
            />

            <RejectionDialog
                isOpen={isRejectionDialogOpen}
                onClose={() => setIsRejectionDialogOpen(false)}
                onReject={handleConfirmReject}
                loading={mutations.isRejecting}
                title="Respingere răspuns"
                description="Introduceți motivul pentru care respingeți această lecție."
                contextInfo={[
                    {
                        label: 'Elev',
                        value: answer?.user?.nume || '-',
                    },
                    {
                        label: 'Lecție',
                        value: answer?.lesson?.title || '-',
                    },
                    {
                        label: 'Data',
                        value: answer?.timestamp
                            ? new Date(answer.timestamp).toLocaleString('ro-RO')
                            : '-',
                    },
                ]}
            />
        </>
    );
}